import routerConstants from '@/constants/routerConstants'
import type { ProductFieldOption, ProductModuleConfig } from '@/interfaces/productModuleConfig'
import type { HangHoaLoai } from '@/interfaces/productInterfaces'
import {
  TABLE_COL_CODE_M,
} from '@/components/common/tableList/tableListSizing'
import {
  TABLE_LIST_META_DATETIME_COL,
  TABLE_LIST_META_ON_OFF_COL,
  TABLE_LIST_META_STATUS_COL,
} from '@/components/common/tableList/tableListMetaCols'

const statusOptions: ProductFieldOption[] = [
  { value: 'all', labelKey: 'status.all' },
  { value: true, labelKey: 'trangThai.dangKinhDoanh' },
  { value: false, labelKey: 'trangThai.ngungKinhDoanh' },
]

const yesNoAllOptions: ProductFieldOption[] = [
  { value: 'all', labelKey: 'filters.all' },
  { value: true, labelKey: 'common.yes' },
  { value: false, labelKey: 'common.no' },
]

/** Lọc Loại hàng: chỉ Hàng hóa + Combo (bỏ Dịch vụ / Sản xuất). */
const loaiHangOptions: ProductFieldOption[] = [
  { value: 'all', labelKey: 'filters.all' },
  { value: 'hangHoa', labelKey: 'loaiHang.hangHoa' },
  { value: 'combo', labelKey: 'loaiHang.combo' },
]

export const loaiHangFormOptions: ProductFieldOption[] = loaiHangOptions.filter(
  (o) => o.value !== 'all',
)

const donViTrongLuongOptions: ProductFieldOption[] = [
  { value: 'kg', labelKey: 'donViTrongLuong.kg' },
  { value: 'g', labelKey: 'donViTrongLuong.g' },
  { value: 'mg', labelKey: 'donViTrongLuong.mg' },
]

const loaiBaoHanhOptions: ProductFieldOption[] = [
  { value: 'baoHanh', labelKey: 'loaiBaoHanh.baoHanh' },
  { value: 'baoTri', labelKey: 'loaiBaoHanh.baoTri' },
  { value: 'caHai', labelKey: 'loaiBaoHanh.caHai' },
]

const phamViOptions: ProductFieldOption[] = [
  { value: 'toanDuAn', labelKey: 'phamVi.toanDuAn' },
  { value: 'theoChiNhanh', labelKey: 'phamVi.theoChiNhanh' },
]

const tonKhoOptions: ProductFieldOption[] = [
  { value: 'all', labelKey: 'filters.all' },
  { value: 'conHang', labelKey: 'tonKho.conHang' },
  { value: 'hetHang', labelKey: 'tonKho.hetHang' },
]

/**
 * Phân loại field theo loại hàng hóa — dùng cho prop `loaiHangs` của formField.
 * - `HANG_VAT_LY`: hàng có tồn kho + trọng lượng + ĐVT quy đổi.
 * - `CO_BAO_HANH`: có bảo hành (trừ Combo).
 * - `CO_DON_VI_CO_BAN`: có ĐVT cơ bản (trừ Combo — combo theo thành phần).
 */
const HANG_VAT_LY: HangHoaLoai[] = ['hangHoa']
export { HANG_VAT_LY }
const CO_BAO_HANH: HangHoaLoai[] = ['hangHoa']
const CO_DON_VI_CO_BAN: HangHoaLoai[] = ['hangHoa']
export { CO_DON_VI_CO_BAN }

export const PRODUCT_MODULE_CONFIG: ProductModuleConfig = {
  moduleKey: 'product',
  sectionKey: 'products',
  endpoint: '/hang-hoa',
  supportsImport: false,
  titleKey: 'sections.products.title',
  codeKey: 'code',
  displayNameKey: 'name',
  routes: {
    list: routerConstants.product.list,
    edit: routerConstants.product.edit,
  },
  columns: [
    { key: 'code', labelKey: 'fields.code', minWidth: '100px', width: '112px' },
    { key: 'maVach', labelKey: 'fields.maVach', minWidth: '120px', width: '132px', optional: true },
    { key: 'maQR', labelKey: 'fields.maQR', minWidth: '120px', width: '132px', optional: true },
    { key: 'name', labelKey: 'fields.name', minWidth: '220px', width: '260px' },
    { key: 'giaBan', labelKey: 'fields.giaBan', minWidth: '120px', width: '130px', type: 'number', optional: true },
    { key: 'giaVon', labelKey: 'fields.giaVon', minWidth: '120px', width: '130px', type: 'number', optional: true },
    { key: 'tonKho', labelKey: 'fields.tonKho', minWidth: '72px', width: '80px', type: 'number', optional: true },
    { key: 'donViCoBan', labelKey: 'fields.donViCoBan', minWidth: '90px', width: '100px' },
    { key: 'giaTriTonKho', labelKey: 'fields.giaTriTonKho', minWidth: '130px', width: '140px', type: 'number' },
    { key: 'quyCachThung', labelKey: 'fields.quyCachThung', minWidth: '120px', width: '130px', type: 'number' },
    { key: 'theTichThung', labelKey: 'fields.theTichThung', minWidth: '120px', width: '130px', type: 'number' },
    { key: 'trongLuong', labelKey: 'fields.trongLuong', minWidth: '140px', width: '150px', type: 'number' },
    { key: 'tongTrongLuongTon', labelKey: 'fields.tongTrongLuongTon', minWidth: '160px', width: '170px', type: 'number' },
    { key: 'tongTheTichTon', labelKey: 'fields.tongTheTichTon', minWidth: '150px', width: '160px', type: 'number' },
    { key: 'viTriSanPham', labelKey: 'fields.viTriSanPham', minWidth: '180px', width: '220px' },
    {
      key: 'khachDat',
      labelKey: 'fields.khachDat',
      minWidth: '72px',
      width: '80px',
      type: 'number',
      numberAccentPositive: true,
      optional: true,
    },
    { key: 'maNhomHang', labelKey: 'filters.maNhomHang', ...TABLE_COL_CODE_M, optional: true },
    { key: 'nhomHangLabel', labelKey: 'filters.tenNhomHang', minWidth: '200px', width: '240px', optional: true },
    {
      key: 'loaiHang',
      labelKey: 'fields.loaiHang',
      minWidth: '120px',
      width: '140px',
      type: 'loaiHang',
      optional: true,
    },
    { key: 'banTrucTiep', labelKey: 'fields.banTrucTiep', minWidth: '110px', width: '120px', type: 'boolean', optional: true },
    { key: 'baoHanh', labelKey: 'fields.baoHanh', minWidth: '100px', width: '110px', type: 'boolean', optional: true },
    { key: 'note', labelKey: 'fields.note', minWidth: '180px', width: '220px', optional: true },
    { key: 'chieuDai', labelKey: 'fields.chieuDai', minWidth: '100px', width: '110px', type: 'number', optional: true },
    { key: 'chieuRong', labelKey: 'fields.chieuRong', minWidth: '100px', width: '110px', type: 'number', optional: true },
    { key: 'chieuCao', labelKey: 'fields.chieuCao', minWidth: '100px', width: '110px', type: 'number', optional: true },
    /** Meta — trạng thái → ngày tạo → ngày cập nhật → bật/tắt (trước cột thao tác). */
    {
      key: 'trangThai',
      labelKey: 'fields.status',
      type: 'status',
      ...TABLE_LIST_META_STATUS_COL,
      /** Nhãn «Đang/Ngừng kinh doanh» dài hơn chip hoạt động mặc định. */
      minWidth: '148px',
      width: '148px',
      maxWidth: '148px',
    },
    { key: 'createdAt', labelKey: 'fields.createdAt', type: 'datetime', ...TABLE_LIST_META_DATETIME_COL },
    { key: 'updatedAt', labelKey: 'fields.updatedAt', type: 'datetime', ...TABLE_LIST_META_DATETIME_COL },
    {
      key: 'onOff',
      labelKey: 'fields.onOffAction',
      type: 'statusToggle',
      ...TABLE_LIST_META_ON_OFF_COL,
      /** Nút «Mở/Ngừng kinh doanh» dài hơn nút Bật/Tắt mặc định. */
      minWidth: '156px',
      width: '156px',
      maxWidth: '156px',
    },
  ],
  formFields: [
    { key: 'code', labelKey: 'fields.code' },
    { key: 'name', labelKey: 'fields.name', required: true },
    { key: 'tenKhaiBaoVi', labelKey: 'fields.tenKhaiBaoVi', type: 'textarea' },
    { key: 'tenKhaiBaoEn', labelKey: 'fields.tenKhaiBaoEn', type: 'textarea' },
    { key: 'nhomHangId', labelKey: 'fields.nhomHang', required: true, type: 'parentCategorySearch' },
    { key: 'nhaCungCapId', labelKey: 'fields.nhaCungCapId', type: 'nhaCungCapSearch' },
    {
      key: 'isActive',
      labelKey: 'fields.status',
      type: 'select',
      options: statusOptions.filter((x) => x.value !== 'all'),
    },
    { key: 'thuongHieuId', labelKey: 'fields.thuongHieuId', type: 'thuongHieuSearch' },
    { key: 'viTriId', labelKey: 'fields.viTriId', type: 'viTriSearch', loaiHangs: HANG_VAT_LY },
    { key: 'phamVi', labelKey: 'fields.phamVi', type: 'select', options: phamViOptions },
    { key: 'danhSachChiNhanhIds', labelKey: 'fields.danhSachChiNhanhIds', type: 'text' },
    { key: 'giaBan', labelKey: 'fields.giaBan', type: 'number' },
    { key: 'giaVon', labelKey: 'fields.giaVon', type: 'number' },
    { key: 'tonKho', labelKey: 'fields.tonKho', type: 'number', loaiHangs: HANG_VAT_LY },
    { key: 'trongLuong', labelKey: 'fields.trongLuong', type: 'number', loaiHangs: HANG_VAT_LY },
    { key: 'dinhMucTonThapNhat', labelKey: 'fields.dinhMucTonThapNhat', type: 'number', loaiHangs: HANG_VAT_LY },
    { key: 'dinhMucTonCaoNhat', labelKey: 'fields.dinhMucTonCaoNhat', type: 'number', loaiHangs: HANG_VAT_LY },
    {
      key: 'donViTrongLuong',
      labelKey: 'fields.donViTrongLuong',
      type: 'select',
      options: donViTrongLuongOptions,
      loaiHangs: HANG_VAT_LY,
    },
    { key: 'chieuDai', labelKey: 'fields.chieuDai', type: 'number', loaiHangs: HANG_VAT_LY },
    { key: 'chieuRong', labelKey: 'fields.chieuRong', type: 'number', loaiHangs: HANG_VAT_LY },
    { key: 'chieuCao', labelKey: 'fields.chieuCao', type: 'number', loaiHangs: HANG_VAT_LY },
    { key: 'canNangKien', labelKey: 'fields.canNangKien', type: 'number', loaiHangs: HANG_VAT_LY },
    { key: 'quyCachKien', labelKey: 'fields.quyCachKien', type: 'text', loaiHangs: HANG_VAT_LY },
    { key: 'quyCachThung', labelKey: 'fields.quyCachThung', type: 'number', loaiHangs: HANG_VAT_LY },
    { key: 'theTichThung', labelKey: 'fields.theTichThung', type: 'number', loaiHangs: HANG_VAT_LY },
    {
      key: 'banTrucTiep',
      labelKey: 'fields.banTrucTiep',
      type: 'select',
      options: yesNoAllOptions.filter((x) => x.value !== 'all'),
    },
    {
      key: 'baoHanh',
      labelKey: 'fields.baoHanh',
      type: 'select',
      options: yesNoAllOptions.filter((x) => x.value !== 'all'),
      loaiHangs: CO_BAO_HANH,
    },
    { key: 'thoiGianBaoHanh', labelKey: 'fields.thoiGianBaoHanh', type: 'number', loaiHangs: CO_BAO_HANH },
    { key: 'loaiBaoHanh', labelKey: 'fields.loaiBaoHanh', type: 'select', options: loaiBaoHanhOptions, loaiHangs: CO_BAO_HANH },
    { key: 'moTa', labelKey: 'fields.moTa', type: 'textarea' },
    { key: 'note', labelKey: 'fields.note', type: 'textarea' },
    {
      key: 'danhSachThanhPhan',
      labelKey: 'fields.danhSachThanhPhan',
      type: 'comboThanhPhan',
      loaiHangs: ['combo'],
    },
    { key: 'hinhAnh', labelKey: 'fields.hinhAnh', type: 'image' },
  ],
  filterFields: [
    { key: 'maHangHoa', labelKey: 'filters.maHangHoa' },
    { key: 'maVach', labelKey: 'filters.maVach' },
    { key: 'tenHangHoa', labelKey: 'filters.tenHangHoa' },
    { key: 'maNhomHang', labelKey: 'filters.maNhomHang' },
    { key: 'tenNhomHang', labelKey: 'filters.tenNhomHang' },
    { key: 'ngayTaoTu', labelKey: 'filters.ngayTaoTu', type: 'date' },
    { key: 'ngayTaoDen', labelKey: 'filters.ngayTaoDen', type: 'date' },
    { key: 'nhomHangId', labelKey: 'fields.nhomHang', type: 'nhomHang' },
    { key: 'loaiHang', labelKey: 'filters.loaiHang', type: 'select', options: loaiHangOptions },
    { key: 'isActive', labelKey: 'fields.status', type: 'select', options: statusOptions },
    { key: 'tonKho', labelKey: 'fields.tonKho', type: 'select', options: tonKhoOptions },
    { key: 'yeuThich', labelKey: 'fields.yeuThich', type: 'select', options: yesNoAllOptions },
    { key: 'banTrucTiep', labelKey: 'fields.banTrucTiep', type: 'select', options: yesNoAllOptions },
    { key: 'baoHanh', labelKey: 'fields.baoHanh', type: 'select', options: yesNoAllOptions },
    {
      key: 'quanLyTheoHanSuDung',
      labelKey: 'tonKhoSection.quanLyTheoHanSuDung',
      type: 'select',
      options: yesNoAllOptions,
    },
    {
      key: 'sortField',
      labelKey: 'filters.sortBy',
      type: 'select',
      options: [
        { value: 'tenHangHoa', labelKey: 'fields.name' },
        { value: 'maHangHoa', labelKey: 'fields.code' },
        { value: 'giaBan', labelKey: 'fields.giaBan' },
        { value: 'giaVon', labelKey: 'fields.giaVon' },
        { value: 'tonKho', labelKey: 'fields.tonKho' },
        { value: 'ngayTao', labelKey: 'fields.createdAt' },
      ],
    },
    {
      key: 'sortOrder',
      labelKey: 'filters.sortOrder',
      type: 'select',
      options: [
        { value: 'asc', labelKey: 'filters.asc' },
        { value: 'desc', labelKey: 'filters.desc' },
      ],
    },
  ],
  importFields: [{ key: 'code', labelKey: 'fields.code' }, { key: 'name', labelKey: 'fields.name', required: true }],
}
